import React, { useState, useEffect, useCallback, useRef } from 'react';
import useScheduleLogic from '../hooks/useScheduleLogic';
import ConnectionError from './ConnectionError';
import LessonNoteModal from './LessonNoteModal';
import { COURSES, WEEKDAYS } from '../utils/constants';
import { isCurrentLesson, getLessonDateForWeek, isCurrentDay } from '../utils/scheduleUtils';
import { formatDate, getDateByWeekAndDay } from '../utils/dateUtils';
import { loadAllNotes, getLessonNoteKey, saveNote } from '../utils/notesStorage';
import apiService from '../utils/api';

/* ---- Helpers ---- */

const getLessonTypeIcon = (type) => {
  const t = (type || '').toLowerCase().trim();
  if (t.includes('лек') || t === 'л.' || t === 'лекция')
    return { icon: 'school-outline', color: '#3B82F6', label: 'Лекция', bg: 'rgba(59,130,246,0.12)' };
  if (t.includes('лаб') || t === 'лаб.' || t === 'лабораторная')
    return { icon: 'flask-outline', color: '#8b5cf6', label: 'Лабораторная', bg: 'rgba(139,92,246,0.12)' };
  if (t.includes('практ') || t.includes('пр.') || t === 'пр' || t === 'практическая')
    return { icon: 'people-outline', color: '#10b981', label: 'Практика', bg: 'rgba(16,185,129,0.12)' };
  if (t.includes('конс') || t === 'конс.' || t === 'консультация')
    return { icon: 'chatbubble-outline', color: '#f59e0b', label: 'Консультация', bg: 'rgba(245,158,11,0.12)' };
  if (t.includes('экзамен') || t.includes('экз.'))
    return { icon: 'document-text-outline', color: '#ef4444', label: 'Экзамен', bg: 'rgba(239,68,68,0.12)' };
  if (t.includes('зачет') || t.includes('зач.') || t.includes('зачёт'))
    return { icon: 'checkmark-circle-outline', color: '#10b981', label: 'Зачёт', bg: 'rgba(16,185,129,0.12)' };
  return { icon: 'book-outline', color: 'var(--text-secondary)', label: type || 'Занятие', bg: 'var(--surface-tertiary)' };
};

/* ---- LessonCountdown ---- */

const LessonCountdown = ({ timeEnd }) => {
  const [remaining, setRemaining] = useState('');
  useEffect(() => {
    const calc = () => {
      const now = new Date();
      const [h, m] = timeEnd.split(':').map(Number);
      const end = new Date(now); end.setHours(h, m, 0, 0);
      const diff = end - now;
      if (diff <= 0) { setRemaining(''); return; }
      const hrs = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      const pad = n => n < 10 ? '0' + n : '' + n;
      if (hrs > 0) setRemaining(`${hrs} ч ${pad(mins)} мин`);
      else if (mins > 0) setRemaining(`${mins} мин ${pad(secs)} с`);
      else setRemaining(`${secs} с`);
    };
    calc(); const iv = setInterval(calc, 1000); return () => clearInterval(iv);
  }, [timeEnd]);
  if (!remaining) return null;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--accent-glass)', padding: '4px 8px', borderRadius: 10 }}>
      <ion-icon name="hourglass-outline" style={{ fontSize: 13, color: 'var(--accent)' }}></ion-icon>
      <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>{remaining}</span>
    </div>
  );
};

/* ---- Main ---- */

const ScheduleScreen = ({ theme, accentColor, isNewYearMode }) => {
  const {
    course, setCourse, groups, selectedGroup, setSelectedGroup,
    scheduleData, pairsTime, loadingGroups, loadingSchedule,
    viewMode, setViewMode, currentDate, setCurrentDate,
    currentWeek, setCurrentWeek,
    error, isOnline, showCachedData,
    currentTime, navigateDate, handleRetry, onRefresh,
  } = useScheduleLogic();

  const [availableCourses, setAvailableCourses] = useState(COURSES);
  const [lessonNotes, setLessonNotes] = useState({});
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [noteModalLesson, setNoteModalLesson] = useState(null);
  const [noteModalWeekday, setNoteModalWeekday] = useState(null);
  const [slideDirection, setSlideDirection] = useState(null); // 'left' | 'right' | null
  const [slideKey, setSlideKey] = useState(0);

  // Schedule format: 'student' | 'teacher' | 'auditory'
  const [scheduleFormat, setScheduleFormat] = useState(() => localStorage.getItem('schedule_format') || 'student');
  const [teacherName, setTeacherName] = useState(() => localStorage.getItem('teacher_name') || '');
  const [auditoryName, setAuditoryName] = useState(() => localStorage.getItem('auditory_name') || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [teacherSchedule, setTeacherSchedule] = useState(null);
  const [auditorySchedule, setAuditorySchedule] = useState(null);
  const [altLoading, setAltLoading] = useState(false);
  const searchTimeout = useRef(null);

  useEffect(() => { setLessonNotes(loadAllNotes()); }, []);
  const refreshNotes = useCallback(() => { setLessonNotes(loadAllNotes()); }, []);

  useEffect(() => {
    apiService.getCourses().then(r => {
      const data = r?.data || r;
      if (data?.courses) {
        const filtered = COURSES.filter(c => data.courses.includes(c.id.toString()));
        if (filtered.length > 0) setAvailableCourses(filtered);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const savedGroup = localStorage.getItem('selected_group');
    if (savedGroup) { /* group restored from storage */ }
  }, []);

  // Search debounce for teacher/auditory
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    const minLen = scheduleFormat === 'teacher' ? 2 : 1;
    if (searchQuery.length < minLen) { setSearchResults([]); return; }
    setSearchLoading(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const result = await apiService.search(searchQuery);
        const data = result?.data || result;
        if (scheduleFormat === 'teacher') {
          setSearchResults(data?.tnames || []);
        } else {
          setSearchResults(data?.auditories || []);
        }
      } catch { setSearchResults([]); }
      setSearchLoading(false);
    }, 400);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchQuery, scheduleFormat]);

  // Fetch teacher/auditory schedule when name or week changes
  const fetchTeacherScheduleData = useCallback(async (name) => {
    if (!name || !currentWeek) return;
    setAltLoading(true);
    try {
      const result = await apiService.getTeacherSchedule(name, currentWeek);
      const data = result?.data || result;
      setTeacherSchedule(data);
    } catch { setTeacherSchedule(null); }
    setAltLoading(false);
  }, [currentWeek]);

  const fetchAuditoryScheduleData = useCallback(async (name) => {
    if (!name || !currentWeek) return;
    setAltLoading(true);
    try {
      const result = await apiService.getAuditorySchedule(name, currentWeek);
      const data = result?.data || result;
      setAuditorySchedule(data);
    } catch { setAuditorySchedule(null); }
    setAltLoading(false);
  }, [currentWeek]);

  useEffect(() => {
    if (scheduleFormat === 'teacher' && teacherName) fetchTeacherScheduleData(teacherName);
    if (scheduleFormat === 'auditory' && auditoryName) fetchAuditoryScheduleData(auditoryName);
  }, [scheduleFormat, teacherName, auditoryName, currentWeek, fetchTeacherScheduleData, fetchAuditoryScheduleData]);

  const handleFormatChange = (fmt) => {
    setScheduleFormat(fmt);
    localStorage.setItem('schedule_format', fmt);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSelectSearchItem = (name) => {
    if (scheduleFormat === 'teacher') {
      setTeacherName(name);
      localStorage.setItem('teacher_name', name);
    } else {
      setAuditoryName(name);
      localStorage.setItem('auditory_name', name);
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleCourseSelect = (cid) => {
    setCourse(cid);
    setSelectedGroup(null);
  };
  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
  };
  const getTimeForLesson = (timeNum) => {
    if (!pairsTime || !Array.isArray(pairsTime)) return null;
    return pairsTime.find(p => p && String(p.time || p.pair_number) === String(timeNum));
  };
  const hasNoteForLesson = (lesson, weekday) => {
    const g = lesson.group ? (Array.isArray(lesson.group) ? lesson.group.join(',') : lesson.group) : '';
    const key = getLessonNoteKey(lesson.subject, weekday, lesson.time, g);
    const note = lessonNotes[key];
    return note && (note.noteText || note.homework);
  };
  const getNotePreview = (lesson, weekday) => {
    const g = lesson.group ? (Array.isArray(lesson.group) ? lesson.group.join(',') : lesson.group) : '';
    return lessonNotes[getLessonNoteKey(lesson.subject, weekday, lesson.time, g)] || null;
  };
  const openNoteModal = (lesson, weekday) => { setNoteModalLesson(lesson); setNoteModalWeekday(weekday); setNoteModalVisible(true); };
  const handleNoteModalClose = (saved) => { setNoteModalVisible(false); setNoteModalLesson(null); setNoteModalWeekday(null); if (saved) refreshNotes(); };

  // Сохранение заметки/дз
  const handleNoteSave = (data) => {
    if (!noteModalLesson || noteModalWeekday == null) return;
    const g = noteModalLesson.group ? (Array.isArray(noteModalLesson.group) ? noteModalLesson.group.join(',') : noteModalLesson.group) : '';
    saveNote({
      subject: noteModalLesson.subject,
      weekday: noteModalWeekday,
      timeSlot: noteModalLesson.time,
      group: g,
      noteText: data.note,
      homework: data.homework
    });
    refreshNotes();
  };
  const changeDate = (d) => { setSlideDirection(d > 0 ? 'left' : 'right'); setSlideKey(k => k + 1); navigateDate(d); };
  const changeWeek = (w) => { const n = currentWeek + w; if (n >= 1) { setSlideDirection(w > 0 ? 'left' : 'right'); setSlideKey(k => k + 1); setCurrentWeek(n); } };

  if (error && !scheduleData) {
    return <ConnectionError type={error} loading={loadingSchedule} onRetry={handleRetry} theme={theme} contentType="schedule" isNewYearMode={isNewYearMode} />;
  }

  /* ---- Lesson Card (matches mobile) ---- */
  const renderLessonCard = (lesson, pairTime, isCurrent, weekday, lessonDate, showGroup = false, cardIndex = 0) => {
    const typeInfo = getLessonTypeIcon(lesson.type_lesson || lesson.type || lesson.lesson_type);
    const noteData = weekday != null ? getNotePreview(lesson, weekday) : null;
    const hasNote = noteData && (noteData.noteText || noteData.homework);
    const groupStr = lesson.group ? (Array.isArray(lesson.group) ? lesson.group.join(', ') : lesson.group) : '';
    return (
      <div key={`${lesson.id || ''}-${lesson.time}-${lesson.subject}-${weekday}`}
        className="card-stagger"
        style={{ display: 'flex', borderRadius: 16, overflow: 'hidden',
          animationDelay: `${cardIndex * 60}ms`,
          border: isCurrent ? '1.5px solid var(--accent)' : '0.5px solid var(--border)',
          background: isCurrent ? 'var(--accent-glass)' : 'var(--surface-secondary)',
          boxShadow: isCurrent ? '0 4px 12px var(--shadow-strong)' : '0 2px 6px var(--shadow-color)',
          marginBottom: 10 }}>
        <div style={{ width: 4, background: typeInfo.color, flexShrink: 0 }} />
        <div style={{ flex: 1, padding: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {pairTime ? (<>
                <ion-icon name="time-outline" style={{ fontSize: 15, color: isCurrent ? 'var(--accent)' : 'var(--text-secondary)' }}></ion-icon>
                <span style={{ fontSize: 14, fontWeight: 600, color: isCurrent ? 'var(--accent)' : 'var(--text)' }}>{pairTime.time_start} – {pairTime.time_end}</span>
              </>) : <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Пара {lesson.time}</span>}
              {isCurrent && <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 8, background: 'var(--accent-glass)', fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>Сейчас</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: typeInfo.bg, padding: '4px 10px', borderRadius: 10 }}>
              <ion-icon name={typeInfo.icon} style={{ fontSize: 13, color: typeInfo.color }}></ion-icon>
              <span style={{ fontSize: 12, color: typeInfo.color, fontWeight: 500 }}>{typeInfo.label}</span>
            </div>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4, marginBottom: 10 }}>{lesson.subject}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {lesson.teacher && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 26, height: 26, borderRadius: 13, background: 'var(--surface-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ion-icon name="person-outline" style={{ fontSize: 14, color: 'var(--text-secondary)' }}></ion-icon>
                </div>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{lesson.teacher}</span>
              </div>
            )}
            {lesson.auditory && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 26, height: 26, borderRadius: 13, background: 'var(--surface-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ion-icon name="location-outline" style={{ fontSize: 14, color: 'var(--text-secondary)' }}></ion-icon>
                </div>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{lesson.auditory}</span>
              </div>
            )}
            {showGroup && groupStr && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 26, height: 26, borderRadius: 13, background: 'var(--surface-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ion-icon name="people-outline" style={{ fontSize: 14, color: 'var(--text-secondary)' }}></ion-icon>
                </div>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{groupStr}</span>
              </div>
            )}
          </div>
          {hasNote && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {noteData?.noteText && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, background: 'var(--accent-glass)', borderRadius: 10, padding: 8 }}>
                  <ion-icon name="document-text-outline" style={{ fontSize: 14, color: 'var(--accent)', marginTop: 2 }}></ion-icon>
                  <span style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{noteData.noteText}</span>
                </div>
              )}
              {noteData?.homework && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, background: 'rgba(245,158,11,0.10)', borderRadius: 10, padding: 8 }}>
                  <ion-icon name="school-outline" style={{ fontSize: 14, color: '#f59e0b', marginTop: 2 }}></ion-icon>
                  <span style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{noteData.homework}</span>
                </div>
              )}
            </div>
          )}
          {weekday != null && (
            <div style={{ display: 'flex', justifyContent: isCurrent && pairTime?.time_end ? 'space-between' : 'flex-end', alignItems: 'center', marginTop: 8 }}>
              {isCurrent && pairTime?.time_end && <LessonCountdown timeEnd={pairTime.time_end} />}
              <button onClick={(e) => { e.stopPropagation(); openNoteModal(lesson, weekday); }}
                style={{ width: 30, height: 30, borderRadius: 15, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: hasNote ? 'var(--accent-glass)' : 'var(--surface-tertiary)' }}>
                <ion-icon name={hasNote ? 'create' : 'create-outline'} style={{ fontSize: 16, color: hasNote ? 'var(--accent)' : 'var(--text-secondary)' }}></ion-icon>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDayHeader = (weekday, date, isCurrent) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingLeft: 4 }}>
      {isCurrent && <div style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--accent)' }} />}
      <span style={{ fontSize: 17, fontWeight: 700, color: isCurrent ? 'var(--accent)' : 'var(--text)' }}>{WEEKDAYS[weekday - 1]}</span>
      <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{formatDate(date)}</span>
      {isCurrent && <span style={{ padding: '2px 8px', borderRadius: 8, background: 'var(--accent-glass)', fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>Сегодня</span>}
    </div>
  );

  const renderEmptyDay = () => (
    <div style={{ background: 'var(--surface-secondary)', borderRadius: 16, padding: 20, textAlign: 'center', border: '0.5px solid var(--border)' }}>
      <ion-icon name="sunny-outline" style={{ fontSize: 28, color: 'var(--text-secondary)', display: 'block', margin: '0 auto 6px' }}></ion-icon>
      <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Занятий нет</span>
    </div>
  );

  const renderWeekDaySchedule = (day) => {
    if (!day || !day.lessons) return null;
    const date = getDateByWeekAndDay(currentWeek, day.weekday);
    const isCur = isCurrentDay(currentWeek, day.weekday, currentTime);
    return (
      <div key={day.weekday} style={{ marginBottom: 20 }}>
        {renderDayHeader(day.weekday, date, isCur)}
        {day.lessons.length > 0 ? day.lessons.map((lesson, idx) => {
          if (!lesson) return null;
          const pt = getTimeForLesson(lesson.time);
          const ld = getLessonDateForWeek(currentWeek, day.weekday, currentTime);
          return renderLessonCard(lesson, pt, isCurrentLesson(lesson, pt, currentTime, ld), day.weekday, date, false, idx);
        }) : renderEmptyDay()}
      </div>
    );
  };

  const renderDailySchedule = () => {
    if (!scheduleData) return null;
    const wd = currentDate.getDay() === 0 ? 7 : currentDate.getDay();
    const ds = scheduleData.days ? scheduleData.days.find(d => d && d.weekday === wd) : { lessons: scheduleData.lessons || [] };
    if (!ds) return renderEmptyDay();
    const now = new Date();
    const isToday = currentDate.getDate() === now.getDate() && currentDate.getMonth() === now.getMonth() && currentDate.getFullYear() === now.getFullYear();
    return (
      <div style={{ marginBottom: 16 }}>
        {renderDayHeader(wd, currentDate, isToday)}
        {ds.lessons && ds.lessons.length > 0 ? ds.lessons.map((lesson, idx) => {
          if (!lesson) return null;
          const pt = getTimeForLesson(lesson.time);
          return renderLessonCard(lesson, pt, isCurrentLesson(lesson, pt, currentTime, currentDate), wd, currentDate, false, idx);
        }) : renderEmptyDay()}
      </div>
    );
  };

  const isMatrix = theme === 'matrix';

  return (
    <div className="screen">
      {/* Schedule Format Selector */}
      <div className="glass-segment" style={{ marginBottom: 16 }}>
        {[
          { id: 'student', label: 'Студентам', icon: 'school-outline' },
          { id: 'teacher', label: 'Преподавателям', icon: 'person-outline' },
          { id: 'auditory', label: 'Аудитория', icon: 'location-outline' },
        ].map(fmt => (
          <button key={fmt.id}
            className={`glass-segment__item ${scheduleFormat === fmt.id ? 'glass-segment__item--active' : ''}`}
            onClick={() => handleFormatChange(fmt.id)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <ion-icon name={fmt.icon} style={{ fontSize: 14 }}></ion-icon>
            {fmt.label}
          </button>
        ))}
      </div>

      {/* ===== TEACHER / AUDITORY MODE ===== */}
      {scheduleFormat !== 'student' && (<>
        {/* Search input */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingLeft: 4 }}>
            <div style={{ width: 28, height: 28, borderRadius: 14, background: 'var(--accent-glass)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ion-icon name={scheduleFormat === 'teacher' ? 'person-outline' : 'location-outline'} style={{ fontSize: 15, color: 'var(--accent)' }}></ion-icon>
            </div>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
              {scheduleFormat === 'teacher' ? 'Преподаватель' : 'Аудитория'}
            </span>
          </div>

          {/* Current selection */}
          {((scheduleFormat === 'teacher' && teacherName) || (scheduleFormat === 'auditory' && auditoryName)) && (
            <div style={{ background: 'var(--accent-glass)', border: '0.5px solid var(--accent-glass-border)', borderRadius: 16, padding: '10px 14px', display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 15, background: 'var(--accent-glass)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}>
                <ion-icon name={scheduleFormat === 'teacher' ? 'person' : 'location'} style={{ fontSize: 16, color: 'var(--accent)' }}></ion-icon>
              </div>
              <span style={{ flex: 1, fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>
                {scheduleFormat === 'teacher' ? teacherName : auditoryName}
              </span>
              <button onClick={() => {
                if (scheduleFormat === 'teacher') { setTeacherName(''); localStorage.removeItem('teacher_name'); setTeacherSchedule(null); }
                else { setAuditoryName(''); localStorage.removeItem('auditory_name'); setAuditorySchedule(null); }
              }} style={{ padding: '4px 12px', borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--accent-glass)', color: 'var(--accent)', fontSize: 12, fontWeight: 500, fontFamily: 'inherit' }}>Изменить</button>
            </div>
          )}

          {/* Search field */}
          {((scheduleFormat === 'teacher' && !teacherName) || (scheduleFormat === 'auditory' && !auditoryName)) && (
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', background: 'var(--surface-secondary)', borderRadius: 14, border: '0.5px solid var(--border)', padding: '0 14px' }}>
                <ion-icon name="search-outline" style={{ fontSize: 18, color: 'var(--text-tertiary)', flexShrink: 0 }}></ion-icon>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (scheduleFormat === 'teacher' && val.length > 0) {
                      val = val.charAt(0).toUpperCase() + val.slice(1);
                    }
                    setSearchQuery(val);
                  }}
                  placeholder={scheduleFormat === 'teacher' ? 'Введите ФИО преподавателя...' : 'Введите номер аудитории...'}
                  style={{
                    flex: 1, padding: '12px 10px', border: 'none', background: 'transparent', outline: 'none',
                    color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
                  }}
                />
                {searchLoading && <div className="spinner" style={{ width: 16, height: 16, flexShrink: 0 }} />}
              </div>

              {/* Capitalization hint for teacher search */}
              {scheduleFormat === 'teacher' && !searchQuery && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '8px 12px',
                  background: 'var(--accent-glass)', borderRadius: 10, border: '0.5px solid var(--accent-glass-border)',
                }}>
                  <ion-icon name="information-circle-outline" style={{ fontSize: 16, color: 'var(--accent)', flexShrink: 0 }}></ion-icon>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.3 }}>Фамилию преподавателя необходимо вводить с заглавной буквы</span>
                </div>
              )}

              {/* Search results dropdown */}
              {searchResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4,
                  background: 'var(--background-elevated)', borderRadius: 14, border: '0.5px solid var(--border)',
                  boxShadow: '0 8px 24px var(--shadow-strong)', maxHeight: 240, overflowY: 'auto',
                }}>
                  {searchResults.map((item, idx) => (
                    <button key={idx} onClick={() => handleSelectSearchItem(item)}
                      style={{
                        width: '100%', padding: '12px 16px', border: 'none', borderBottom: idx < searchResults.length - 1 ? '0.5px solid var(--border)' : 'none',
                        background: 'transparent', cursor: 'pointer', textAlign: 'left', color: 'var(--text)', fontSize: 14, fontFamily: 'inherit',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <ion-icon name={scheduleFormat === 'teacher' ? 'person-outline' : 'location-outline'} style={{ fontSize: 18, color: 'var(--accent)' }}></ion-icon>
                        {item}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Week navigator for teacher/auditory */}
        {((scheduleFormat === 'teacher' && teacherName) || (scheduleFormat === 'auditory' && auditoryName)) && currentWeek && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 16, gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--surface-secondary)', borderRadius: 14, border: '0.5px solid var(--border)', padding: 4 }}>
              <button onClick={() => { setSlideDirection('right'); setSlideKey(k => k + 1); setCurrentWeek(w => w - 1); }} style={{ padding: 6, borderRadius: 10, background: 'var(--accent-glass)', border: 'none', cursor: 'pointer', display: 'flex' }}>
                <ion-icon name="chevron-back" style={{ fontSize: 18, color: 'var(--accent)' }}></ion-icon>
              </button>
              <span style={{ color: 'var(--text)', fontSize: 13, margin: '0 8px', fontWeight: 600, minWidth: 60, textAlign: 'center' }}>Нед. {currentWeek}</span>
              <button onClick={() => { setSlideDirection('left'); setSlideKey(k => k + 1); setCurrentWeek(w => w + 1); }} style={{ padding: 6, borderRadius: 10, background: 'var(--accent-glass)', border: 'none', cursor: 'pointer', display: 'flex' }}>
                <ion-icon name="chevron-forward" style={{ fontSize: 18, color: 'var(--accent)' }}></ion-icon>
              </button>
            </div>
          </div>
        )}

        {/* Teacher/Auditory schedule display */}
        {altLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 200, borderRadius: 16 }} />)}
          </div>
        ) : (
          (() => {
            const data = scheduleFormat === 'teacher' ? teacherSchedule : auditorySchedule;
            if (!data?.days) {
              if ((scheduleFormat === 'teacher' && teacherName) || (scheduleFormat === 'auditory' && auditoryName)) {
                return (
                  <div className="empty-state">
                    <ion-icon name="calendar-outline" style={{ fontSize: 48, color: 'var(--text-tertiary)' }}></ion-icon>
                    <p className="empty-state__text">Нет данных на эту неделю</p>
                  </div>
                );
              }
              return (
                <div className="empty-state">
                  <ion-icon name="search-outline" style={{ fontSize: 48, color: 'var(--text-tertiary)' }}></ion-icon>
                  <p className="empty-state__text">{scheduleFormat === 'teacher' ? 'Введите ФИО преподавателя' : 'Введите номер аудитории'}</p>
                </div>
              );
            }
            return <div key={`alt-${slideKey}`} className={slideDirection === 'left' ? 'schedule-slide-left' : slideDirection === 'right' ? 'schedule-slide-right' : 'content-fade-enter'}>{data.days.map(day => {
              if (!day?.lessons) return null;
              const date = getDateByWeekAndDay(currentWeek, day.weekday);
              const isCur = isCurrentDay(currentWeek, day.weekday, currentTime);
              return (
                <div key={day.weekday} style={{ marginBottom: 20 }}>
                  {renderDayHeader(day.weekday, date, isCur)}
                  {day.lessons.length > 0 ? day.lessons.map((lesson, idx) => {
                    const pt = getTimeForLesson(lesson.time);
                    const ld = getLessonDateForWeek(currentWeek, day.weekday, currentTime);
                    const showGroup = scheduleFormat !== 'student';
                    return renderLessonCard(lesson, pt, isCurrentLesson(lesson, pt, currentTime, ld), day.weekday, date, showGroup, idx);
                  }) : renderEmptyDay()}
                </div>
              );
            })}</div>;
          })()
        )}
      </>)}

      {/* ===== STUDENT MODE ===== */}
      {scheduleFormat === 'student' && (<>
          {/* Course buttons */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingLeft: 4 }}>
              <div style={{ width: 28, height: 28, borderRadius: 14, background: 'var(--accent-glass)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ion-icon name="school-outline" style={{ fontSize: 15, color: 'var(--accent)' }}></ion-icon>
              </div>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Курс</span>
            </div>
            <div style={{ display: 'flex', background: 'var(--surface-secondary)', borderRadius: 14, padding: 3, border: '0.5px solid var(--border)' }}>
              {availableCourses.map(c => (
                <button key={c.id} onClick={() => handleCourseSelect(c.id)} style={{
                  flex: 1, padding: '9px 10px', borderRadius: 12, border: 'none', cursor: 'pointer', textAlign: 'center',
                  background: course === c.id ? 'var(--accent)' : 'transparent',
                  color: course === c.id ? '#ffffff' : 'var(--text)',
                  fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                  boxShadow: course === c.id ? '0 2px 4px var(--shadow-color)' : 'none' }}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          {/* Group buttons */}
          {course != null && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingLeft: 4 }}>
                <div style={{ width: 28, height: 28, borderRadius: 14, background: 'var(--accent-glass)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ion-icon name="people-outline" style={{ fontSize: 15, color: 'var(--accent)' }}></ion-icon>
                </div>
                <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Группа</span>
              </div>
              {loadingGroups ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}><div className="spinner" /></div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
                  {groups.map(g => {
                    const name = typeof g === 'string' ? g : (g.name || g.title || String(g));
                    const gId = typeof g === 'string' ? g : (g.id || g.name || g);
                    const selId = typeof selectedGroup === 'string' ? selectedGroup : (selectedGroup?.id || selectedGroup?.name || selectedGroup);
                    const active = selId === gId;
                    return (
                      <button key={name} onClick={() => handleGroupSelect(g)} style={{
                        padding: '10px 18px', borderRadius: 14, cursor: 'pointer', textAlign: 'center',
                        background: active ? 'var(--accent)' : 'var(--surface-secondary)',
                        border: active ? '1.5px solid var(--accent)' : '0.5px solid var(--border)',
                        color: active ? '#ffffff' : 'var(--text)',
                        fontSize: 14, fontWeight: active ? 600 : 500, fontFamily: 'inherit',
                        boxShadow: active ? '0 3px 8px var(--shadow-strong)' : '0 1px 3px var(--shadow-color)' }}>
                        {name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

      {selectedGroup && (
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Расписание для {typeof selectedGroup === 'string' ? selectedGroup : (selectedGroup?.name || selectedGroup?.title || '')}</div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4 }}>{availableCourses.find(c => c.id === course)?.label || ''}</div>
        </div>
      )}

      {selectedGroup && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: 'var(--surface-secondary)', borderRadius: 14, padding: 3, border: '0.5px solid var(--border)' }}>
            {['day', 'week'].map(m => (
              <button key={m} onClick={() => setViewMode(m)} style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: viewMode === m ? (isMatrix ? 'rgba(0,255,65,0.15)' : 'var(--accent)') : 'transparent',
                color: viewMode === m ? (isMatrix ? '#00FF41' : '#fff') : 'var(--text)',
                fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                boxShadow: viewMode === m ? '0 2px 4px var(--shadow-color)' : 'none' }}>
                <ion-icon name={m === 'day' ? 'today-outline' : 'calendar-outline'} style={{ fontSize: 15, color: viewMode === m ? (isMatrix ? '#00FF41' : '#fff') : 'var(--text-secondary)' }}></ion-icon>
                {m === 'day' ? 'День' : 'Неделя'}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', background: 'var(--surface-secondary)', borderRadius: 14, border: '0.5px solid var(--border)', padding: 4 }}>
            <button onClick={() => viewMode === 'day' ? changeDate(-1) : changeWeek(-1)} style={{ padding: 6, borderRadius: 10, background: 'var(--accent-glass)', border: 'none', cursor: 'pointer', display: 'flex' }}>
              <ion-icon name="chevron-back" style={{ fontSize: 18, color: 'var(--accent)' }}></ion-icon>
            </button>
            <span style={{ color: 'var(--text)', fontSize: 13, margin: '0 8px', fontWeight: 600, minWidth: 60, textAlign: 'center' }}>
              {viewMode === 'day' ? formatDate(currentDate) : `Нед. ${currentWeek}`}
            </span>
            <button onClick={() => viewMode === 'day' ? changeDate(1) : changeWeek(1)} style={{ padding: 6, borderRadius: 10, background: 'var(--accent-glass)', border: 'none', cursor: 'pointer', display: 'flex' }}>
              <ion-icon name="chevron-forward" style={{ fontSize: 18, color: 'var(--accent)' }}></ion-icon>
            </button>
          </div>
        </div>
      )}

      {showCachedData && (
        <div style={{ padding: '8px 12px', background: 'var(--accent-glass)', borderRadius: 10, border: '0.5px solid var(--accent-glass-border)', marginBottom: 10, fontSize: 13, color: 'var(--accent)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
          <ion-icon name="cloud-offline-outline" style={{ fontSize: 16 }}></ion-icon>Показаны кэшированные данные
        </div>
      )}

      {selectedGroup && viewMode === 'day' && (loadingSchedule ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: 100, borderRadius: 16 }} />)}
        </div>
      ) : <div key={`day-${slideKey}`} className={slideDirection === 'left' ? 'schedule-slide-left' : slideDirection === 'right' ? 'schedule-slide-right' : 'content-fade-enter'}>{renderDailySchedule()}</div>)}

      {selectedGroup && viewMode === 'week' && (loadingSchedule ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 200, borderRadius: 16 }} />)}
        </div>
      ) : scheduleData?.days ? <div key={`week-${slideKey}`} className={slideDirection === 'left' ? 'schedule-slide-left' : slideDirection === 'right' ? 'schedule-slide-right' : 'content-fade-enter'}>{scheduleData.days.map(day => day?.lessons ? renderWeekDaySchedule(day) : null)}</div> : (
        <div className="empty-state">
          <ion-icon name="calendar-outline" style={{ fontSize: 48, color: 'var(--text-tertiary)' }}></ion-icon>
          <p className="empty-state__text">Нет данных</p>
        </div>
      ))}

      {!selectedGroup && groups.length > 0 && !loadingGroups && (
        <div className="empty-state">
          <ion-icon name="school-outline" style={{ fontSize: 48, color: 'var(--text-tertiary)' }}></ion-icon>
          <p className="empty-state__text">Выберите группу</p>
        </div>
      )}
      </>)}

      <LessonNoteModal
        visible={noteModalVisible}
        onClose={handleNoteModalClose}
        lesson={noteModalLesson}
        weekday={noteModalWeekday}
        theme={theme}
        accentColor={accentColor}
        onSave={handleNoteSave}
      />
    </div>
  );
};

export default ScheduleScreen;
